import type { ImageLoaderProps } from 'next/image';

function getExtension(src?: string): string {
  const regex = /\.(\w+)$/;
  const extMatch = src ? regex.exec(src) : null;
  return extMatch ? extMatch[1].toLowerCase() : '';
}

function isFormatAllowed(ext: string, fileFormats: string): boolean {
  if (!fileFormats) {
    return true;
  }

  const allowedFormats = fileFormats.split(',').map(f => f.trim().toLowerCase());
  return !!ext && allowedFormats.includes(ext);
}

function buildOptimizedUrl(
  src: string,
  width?: number,
  quality?: number,
  defaultQuality?: string,
  defaultFormatConversation?: string,
): string {
  const url = src.startsWith('http') ? new URL(src) : new URL(`https://${src}`);
  const q = quality && quality < 80 ? quality.toString() : defaultQuality;

  if (q) {
    url.searchParams.set('q', q.toString());
  } else {
    url.searchParams.delete('q');
  }

  if (defaultFormatConversation && src.endsWith(defaultFormatConversation)) {
    url.searchParams.delete('fm');
  } else if (defaultFormatConversation) {
    url.searchParams.set('fm', defaultFormatConversation);
  }

  if (width) {
    url.searchParams.set('w', width.toString());
  }
  return url.href;
}

export default function nextImageLoaderFile({ src, width, quality }: Partial<ImageLoaderProps>): string | undefined {
  const fileFormats = process.env.NEXT_PUBLIC_CONTENTFUL_OPTIMIZE_IMAGE_FORMATS || '';
  const defaultQuality = process.env.NEXT_PUBLIC_CONTENTFUL_IMAGE_QUALITY;
  const defaultFormatConversation = process.env.NEXT_PUBLIC_CONTENTFUL_IMAGE_FORMAT;

  const ext = getExtension(src);
  const shouldOptimizeFormat = isFormatAllowed(ext, fileFormats);

  if (!shouldOptimizeFormat) {
    return src;
  }

  if (src?.includes('images.ctfassets.net')) {
    return buildOptimizedUrl(src, width, quality, defaultQuality, defaultFormatConversation);
  }
  return src;
}
